<template>
  <div
    ref="pickrContainer"
    class="d-inline-flex align-items-center picker-container"
  >
    <div ref="pickr" />

    <i
      v-if="copy"
      :class="['cursor-pointer font-xl px-2 bi', copied ? 'bi-check2' : 'bi-copy']"
      title="Copy hex color"
      @click="copyColorToClipboard"
    />
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import Pickr from '@simonwep/pickr'
import {copyToClipboard, rgbToHex} from '@frontend/services/helpers'
import {ThemesContainerSingleton} from '@/banner_template/themes/ThemesContainerSinglton'

const BASIC_COLORS = [
  '#FFFFFF',
  '#000000',
  '#FFFF00',
  '#FF0000',
  '#00FF00',
  '#A52A2A' 
]

const RECENT_KEY = 'pickr_recent_colors'
const MAX_RECENTS = 6

const getRecentColors = () =>
  JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')

const addRecentColor = (color) => {
  let list = getRecentColors()
  list = [color, ...list.filter(c => c !== color)]
  list = list.slice(0, MAX_RECENTS)
  localStorage.setItem(RECENT_KEY, JSON.stringify(list))
}

export default {
  props: {
    value: {type: String, default: '#000000'},
    themes: {type: Array, default: () => []},
    disabled: {type: Boolean, default: false},
    position: {type: String, default: 'bottom-middle'},
    opacity: {type: Boolean, default: false},
    copy: {type: Boolean, default: false},
    isTextEditor: {type: Boolean, default: false}
  },

  data() {
    return {
      pickr: null,
      copied: false,
      isUserChanging: false,
      isProgrammaticUpdate: false,
      isUserPick: false
    }
  },

  watch: {
    value(newVal) {
      if (!this.pickr || this.isUserChanging) return
      const current = this.pickr.getColor().toHEXA().toString()
      if (current !== newVal) {
        this.isProgrammaticUpdate = true
        // silent: a synced value must not come back as an input event — the throttled change
        // handler can run after the flag below is cleared and re-apply it to other cells
        this.pickr.setColor(newVal, true)
        setTimeout(() => (this.isProgrammaticUpdate = false), 0)
      }
    },
    disabled(val) {
      if (!this.pickr) return
      val ? this.pickr.disable() : this.pickr.enable()
    }
  },

  created() {
    this.onColorChange = _.throttle(this.onColorChange, 80)
  },

  mounted() {
    this.$nextTick(this.initPicker)
  },

  beforeDestroy() {
    this.onColorChange.cancel()
    // Clean up Pickr instance to prevent memory leaks
    if (this.pickr) {
      this.pickr.destroyAndRemove();
      this.pickr = null;
    }
  },

  methods: {
    setColor(color) {
      if (!this.pickr) return
      this.isUserPick = true
      this.pickr.setColor(color)
      this.pickr.applyColor()
      this.isUserPick = false
    },

    // Pickr also fires `change` for a programmatic setColor and when its hex input blurs (re-emitting
    // whatever colour it holds — after a selection change that is the newly selected cell's colour).
    // A person can only change the colour through the open popup, so decide at the event whether it
    // came from them; by the time the throttled handler runs, flags set around setColor are stale.
    onPickrChange(color, source, instance) {
      if (this.isProgrammaticUpdate || (!instance.isOpen() && !this.isUserPick)) return
      this.onColorChange(color, source, instance)
    },

    onColorChange(color, source, instance) {
      this.isUserChanging = true
      const hex = color.toHEXA().toString()

      this.$emit('input', hex)
      addRecentColor(hex)
      this.updateRecentBox()

      instance.applyColor()
      setTimeout(() => (this.isUserChanging = false), 0)
    },

    initPicker() {
      // Defensive check: ensure refs exist before initializing Pickr
      if (!this.$refs.pickr || !this.$refs.pickrContainer) {
        console.warn('ColorPicker refs not available, skipping initialization');
        return;
      }

      this.pickr = new Pickr({
        el: this.$refs.pickr,
        container: this.$refs.pickrContainer,
        theme: 'nano',
        default: this.value,
        swatches: [],
        ...(this.isTextEditor
          ? {staticPlacement: this.position}
          : {position: this.position}),
        closeOnScroll: true,
        components: {
          preview: true,
          hue: true,
          opacity: this.opacity,
          interaction: {
            hex: true,
            rgba: true,
            hsla: true,
            input: true
          }
        }
      })

      this.pickr.on('change', this.onPickrChange)
      this.pickr.on('hide', () => {
        // Finish the edit before another cell becomes selected.
        this.onColorChange.flush()
        this.isUserChanging = false
      })
      this.pickr.on('init', this.buildColorBoxes)

      if (window.EyeDropper) {
        const root = this.pickr.getRoot().app
        const eye = document.createElement('a')
        eye.href = '#'
        eye.innerHTML = '<i class="c-icon cil-eyedropper"></i>'
        eye.onclick = (e) => {
          e.preventDefault()
          new EyeDropper().open().then(({sRGBHex}) => {
            if (/^rgb/.test(sRGBHex)) {
              const [r, g, b] = sRGBHex.replace(/[^\d,]/g, '').split(',')
              this.setColor(rgbToHex(+r, +g, +b))
            } else {
              this.setColor(sRGBHex)
            }
          })
        }
        root.querySelector('.pcr-interaction').appendChild(eye)
      }

      if (this.themes.length) {
        const select = document.createElement('select')
        select.className = 'form-control mt-2'
        this.themes.forEach(t => {
          const o = document.createElement('option')
          o.value = t.id
          o.textContent = t.name
          select.appendChild(o)
        })
        select.onchange = e => {
          const theme = ThemesContainerSingleton.getByIdSync(+e.target.value)
          while (this.pickr.removeSwatch(0)) { /* remove all swatches */ }
          BASIC_COLORS.forEach(c => this.pickr.addSwatch(c))
          if (theme) theme.colors.forEach(c => this.pickr.addSwatch(c))
        }
        this.pickr.getRoot().swatches.before(select)
      }
    },

    buildColorBoxes() {
      const root = this.pickr.getRoot().app

      const wrapper = document.createElement('div')
      wrapper.className = 'custom-color-wrapper'

      wrapper.appendChild(this.createBox('Recent colors', 'recent'))
      wrapper.appendChild(this.createBox('Basic colors', 'basic'))

      root.prepend(wrapper)
      this.updateRecentBox()
    },

    createBox(title, type) {
      const box = document.createElement('div')
      box.className = 'color-box'
      box.dataset.type = type

      const label = document.createElement('div')
      label.className = 'color-box-title'
      label.textContent = title

      const grid = document.createElement('div')
      grid.className = 'color-box-grid'

      box.appendChild(label)
      box.appendChild(grid)

      if (type === 'basic') {
        BASIC_COLORS.forEach(color => {
          const btn = this.createSwatch(color)
          grid.appendChild(btn)
        })
      }

      return box
    },

    updateRecentBox() {
      const recent = getRecentColors()
      const box = this.pickr
        .getRoot()
        .app.querySelector('.color-box[data-type="recent"] .color-box-grid')

      if (!box) return
      box.innerHTML = ''

      recent.forEach(color => {
        box.appendChild(this.createSwatch(color))
      })
    },

    createSwatch(color) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'color-swatch'
      btn.style.background = color
      btn.title = color
      btn.onclick = () => this.setColor(color)
      return btn
    },

    copyColorToClipboard() {
      copyToClipboard(this.pickr.getColor().toHEXA().toString())
      this.copied = true
      setTimeout(() => (this.copied = false), 1000)
    }
  }
}
</script>

<style>
.custom-color-wrapper {
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

.color-box {
  margin-bottom: 8px;
}

.color-box-title {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
}

.color-box-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 18px);
  gap: 6px;
}

.color-swatch {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid #aaa;
  cursor: pointer;
}
</style>
